import { useQueries } from '@tanstack/react-query'
import {
  ArrowRight,
  BarChart3,
  Calendar,
  Compass,
  ShieldCheck,
  Sparkles,
  Target,
  WandSparkles,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { AttributionFooter, LimitationsPanel } from '../components/Domain'
import { WhatPanel } from '../components/panels/WhatPanel'
import { WhenPanel } from '../components/panels/WhenPanel'
import { WhoPanel } from '../components/panels/WhoPanel'
import { Alert } from '../components/ui'
import { useApp } from '../context/AppContext'
import { askWhat, askWhen, askWho, getHealth } from '../lib/api'
import type { Answer, Question } from '../lib/types'

/**
 * Portada: explica qué hace la herramienta y da acceso a sus dos vías.
 * Hero + dos botones (Data Explorer y Campaign Studio).
 */
export function Home() {
  const { t } = useTranslation()

  const health = useQueries({ queries: [{ queryKey: ['health'], queryFn: getHealth, staleTime: 30_000 }] })[0]

  return (
    <div className="space-y-10 sm:space-y-12">
      {/* 1. HERO PRINCIPAL: Laponia & Posicionamiento PYME */}
      <section className="relative overflow-hidden rounded-2xl border bg-gradient-to-r from-card via-card/95 to-background shadow-lg">
        <div className="absolute inset-y-0 right-0 w-full lg:w-3/5 pointer-events-none overflow-hidden">
          <img
            src="/images/lapland-hero.jpg"
            alt="Levi Lapland Aurora Borealis"
            className="w-full h-full object-cover object-center opacity-35 dark:opacity-40 select-none"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-card via-card/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-card/50" />
        </div>

        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-gradient-to-br from-teal-500/20 via-cyan-500/15 to-indigo-600/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-tr from-indigo-500/15 to-purple-500/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl 2xl:max-w-4xl p-6 sm:p-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="size-3.5" />
            <span>{t('home.pmeTitle', 'Panel Estratégico de Campañas para PYMES')}</span>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-foreground leading-tight">
            {t('app.tagline')}
          </h1>

          <p className="text-base sm:text-lg text-primary/95 font-medium leading-relaxed">
            {t('home.pmeSubtitle', 'Diagnóstico unificado en un solo vistazo: combina estacionalidad, mercados emisores y oferta para maximizar el retorno de tu inversión publicitaria.')}
          </p>

          <p className="text-xs sm:text-sm text-muted-foreground/90 leading-relaxed">
            {t('app.heroDescription')}
          </p>

          {health.data?.status === 'degraded' ? (
            <Alert variant="warning" className="mt-4">
              <ul className="list-disc pl-4 text-xs">
                {health.data.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </div>
      </section>

      {/* 2. ACCESOS PRINCIPALES */}
      <section className="grid gap-5 md:grid-cols-2">
        {/* Data Explorer */}
        <Link to="/explorer" className="group">
          <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-card p-6 h-full shadow-md transition-all hover:border-primary/60 hover:shadow-lg">
            <div className="flex items-center gap-3.5 mb-3">
              <span className="flex size-12 items-center justify-center rounded-xl bg-primary/15 text-primary shrink-0 transition-transform group-hover:scale-105">
                <BarChart3 className="size-6" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">{t('home.ctaExplorerTitle', 'Data Explorer')}</h2>
                <p className="text-xs text-muted-foreground">{t('home.ctaExplorerSub', 'Explora y diagnostica estacionalidad, mercados y oferta de Levi.')}</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary pt-2">
              {t('home.ctaExplorerBtn', 'Abrir Data Explorer')}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>

        {/* Campaign Studio */}
        <Link to="/studio" className="group">
          <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-card p-6 h-full shadow-md transition-all hover:border-cyan-500/60 hover:shadow-lg">
            <div className="flex items-center gap-3.5 mb-3">
              <span className="flex size-12 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-400 shrink-0 transition-transform group-hover:scale-105">
                <WandSparkles className="size-6" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-foreground group-hover:text-cyan-400 transition-colors">{t('home.ctaStudioTitle', 'Campaign Studio')}</h2>
                <p className="text-xs text-muted-foreground">{t('home.ctaStudioSub', 'Convierte el diagnóstico en un briefing de campaña listo para ejecutar.')}</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-400 pt-2">
              {t('home.ctaStudioBtn', 'Abrir Campaign Studio')}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </Link>
      </section>

      {/* 3. CÓMO AYUDA A TU NEGOCIO LOCAL (EL FLUJO EN 3 PASOS) */}
      <section className="rounded-2xl border bg-card/60 p-6 sm:p-8 backdrop-blur-sm space-y-6">
        <div className="space-y-1.5 text-center max-w-2xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
            {t('home.welcome.howItWorksTitle')}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t('home.welcome.howItWorksSub')}
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-3 pt-2">
          {/* Paso 1 */}
          <div className="rounded-xl border border-border/70 bg-background/70 p-5 space-y-3 relative overflow-hidden group hover:border-primary/50 transition-all">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <Calendar className="size-5" />
              </div>
              <h3 className="font-bold text-sm text-foreground">
                {t('home.welcome.step1Title')}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('home.welcome.step1Desc')}
            </p>
          </div>

          {/* Paso 2 */}
          <div className="rounded-xl border border-border/70 bg-background/70 p-5 space-y-3 relative overflow-hidden group hover:border-primary/50 transition-all">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400 border border-teal-500/20">
                <Compass className="size-5" />
              </div>
              <h3 className="font-bold text-sm text-foreground">
                {t('home.welcome.step2Title')}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('home.welcome.step2Desc')}
            </p>
          </div>

          {/* Paso 3 */}
          <div className="rounded-xl border border-border/70 bg-background/70 p-5 space-y-3 relative overflow-hidden group hover:border-cyan-500/50 transition-all">
            <div className="flex items-center gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                <Target className="size-5" />
              </div>
              <h3 className="font-bold text-sm text-foreground">
                {t('home.welcome.step3Title')}
              </h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {t('home.welcome.step3Desc')}
            </p>
          </div>
        </div>
      </section>



      {/* 5. GARANTÍA DE RIGOR Y PROCEDENCIA DE DATOS */}
      <section className="rounded-xl border border-border/60 bg-muted/20 p-4 sm:p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <ShieldCheck className="size-4 text-emerald-400" />
              {t('home.welcome.trustTitle')}
            </span>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1">
              <span className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                {t('home.welcome.trustOsm')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                {t('home.welcome.trustStatFin')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="size-1.5 rounded-full bg-emerald-400" />
                {t('home.welcome.trustMeteo')}
              </span>
            </div>
          </div>
          <Link to="/sources">
            <span className="text-xs font-semibold text-primary hover:underline shrink-0 flex items-center gap-1">
              {t('nav.sources', 'Fuentes')} & ETL →
            </span>
          </Link>
        </div>
      </section>
    </div>
  )
}

/**
 * Vista de preguntas individuales (/when, /who, /what) mantenidas para enlace directo o deep-dive.
 */
export function QuestionPage({ question }: { question: Question }) {
  const { t } = useTranslation()
  const { locale, audience } = useApp()

  const query = useQueries({
    queries: [
      {
        queryKey: [question, locale, audience],
        queryFn: () => {
          if (question === 'when') return askWhen({ locale, audience, horizon_months: 12 })
          if (question === 'who') return askWho({ locale, audience })
          return askWhat({ locale, audience, limit: 12 })
        },
        staleTime: 300_000,
      },
    ],
  })[0]

  const answer = query.data as Answer | undefined
  const Panel =
    question === 'when' ? WhenPanel : question === 'who' ? WhoPanel : WhatPanel

  return (
    <div className="space-y-6">
      {query.isError ? (
        <Alert variant="danger">
          <p>{t('common.error')}</p>
          <p className="mt-1 font-mono text-xs">
            {query.error instanceof Error ? query.error.message : ''}
          </p>
        </Alert>
      ) : null}
      <Panel answer={answer} loading={query.isLoading} />
      {answer ? (
        <div className="space-y-3">
          <LimitationsPanel
            limitations={answer.limitations}
            missingIndicators={answer.missing_indicators}
          />
          <AttributionFooter answer={answer} />
        </div>
      ) : null}
    </div>
  )
}
